// * IMPORTS *
// * basic imports (react & components)

import { CustomInput as InputForm } from "@/components/Input";
import { ModalLogin } from "@/components/Modal";
import { useAuth } from "@/contexts";

import { useAsyncHasRegister } from "@/hooks/Auth/useAsyncHasRegister";
import { useRegisterPFClient } from "@/hooks/Client";
import { useLocality } from "@/hooks/Client/useLocality";
import { useIsomorphicLayoutEffect } from "@/hooks/Custom";
import { useCpf, useEmpty, useFilter } from "@/utils";
import {
  Box,
  Button,
  Flex,
  Img,
  InputGroup,
  Text,
  useBoolean,
  useBreakpointValue,
  useDisclosure,
  useToast,
  VStack
} from "@chakra-ui/react";
import Router from "next/router";
import { createRef, useEffect, useState } from "react";
import InputComponentEmail from "../Input/inputComponentEmail";

// * Main function *
export function FormPF({ checkout, customWidth }: { checkout: boolean; customWidth: string }) {
  const { signIn } = useAuth();
  // * Setup initial states of application
  const [Document, setDocument] = useState(String); // * CPF
  const DocumentRef = createRef();
  const [Email, setEmail] = useState(String); // * Email
  const [Name, setName] = useState(String); // * Name
  const [Birthdate, setBirthdate] = useState(String); // * Birthdate
  const [Phone, setPhone] = useState(String); // * Phone
  const [Celphone, setCelphone] = useState(String); // * Celphone
  const [Password, setPassword] = useState(String); // * Password
  const [ConfirmPassword, setConfirmPassword] = useState(String); // * Password again
  const [Zipcode, setZipcode] = useState(String); // * Zipcode
  const [Street, setStreet] = useState(String); // * Street
  const [Number, setNumber] = useState(String); // * Number
  const [Complement, setComplement] = useState(String); // * Complement
  const [District, setDistrict] = useState(String); // * District
  const [City, setCity] = useState(String); // * City
  const [State, setState] = useState(String); // * State

  //* Toast default options
  const Toast = useToast({
    isClosable: true,
    position: "bottom",
    duration: 5000
  });
  // * Breakpoint controller
  const WideVersion = useBreakpointValue({
    base: false,
    xs: false,
    sm: false,
    md: false,
    lg: true,
    xl: true
  });

  // * Modal controllers
  const { isOpen, onOpen, onClose } = useDisclosure();

  // * General controllers and Utils
  const [HasRegister, setHasRegister] = useState(true);
  const [Disable, setDisable] = useState(true);
  const [SubmitDisable, setSubmitDisable] = useState(true);
  const [SubmitLoading, setSubmitLoading] = useState(false);
  const [ForwardDisable, setForwardDisable] = useState(true);
  const [ForwardLoading, setForwardLoading] = useState(false);
  const [ExistEmail, setExistEmail] = useState(false);
  const [ExistDoc, setExistDoc] = useState(false);
  const [ExistMsg, setExistMsg] = useState("");
  const [ShowPassword, setShow] = useBoolean();

  // * Make Forward button enabled
  useIsomorphicLayoutEffect(() => {
    let DocumentFiltered = useFilter(Document ?? "", { type: "number" });
    if (
      !useEmpty(Document) &&
      DocumentFiltered.length === 11 &&
      !useEmpty(Email) &&
      Email.length > 5 &&
      Email.indexOf("@") !== -1
    ) {
      setForwardDisable(false);
      setSubmitDisable(false);
    } else {
      setForwardDisable(true);
      setSubmitDisable(true);
    }
  });

  // * Valid CPF and E-mail
  const ValidUser = async (): Promise<void> => {
    const CPF = useCpf(Document);
    if (CPF.valid) {
      setHasRegister(false);
      return;
    } else {
      setHasRegister(true);
      Toast({
        title: "CPF ou Email inválidos",
        description: "Por favor, verifique seus dados e tente novamente.",
        status: "error",
        containerStyle: {
          color: "white",
          fontFamily: "arial",
          fontWeight: "100"
        }
      });
      return;
    }
  };

  // * Verify if user Exists, if not, valid CPF and Email, if exists, show Modal
  const UserExists = async (): Promise<void> => {
    // * Call API to verify if user exists
    // console.log("email", Email);
    setForwardLoading(true);
    const [ExistsData, ExistsError] = await useAsyncHasRegister({
      UserEmail: Email,
      UserDocument: Document,
      isPhysicalPerson: true
    });

    // * If Requisition has error
    if (ExistsData) {
      setExistMsg(ExistsData.msg);
      setExistEmail(ExistsData.email);
      setExistDoc(ExistsData.doc);
      setHasRegister(true);
      setForwardLoading(false);
      onOpen();

      return;
    }

    // * If Requisition has data and user exists, show Modal
    if (ExistsError && ExistsData === null) {
      setHasRegister(false);
      setForwardLoading(false);
      return ValidUser();
    }
  };

  // * Found address by Zipcode
  const SearchAddress = async (): Promise<void> => {
    const Zip = useFilter(Zipcode, { type: "number" });
    // * Call API to verify if user exists
    const [AddressData, AddressError, AddressLoading] = await useLocality({
      zipcode: Zip
    });

    // * If Requisition has error
    if (AddressError) {
      Toast({
        title: AddressError,
        status: "error",
        containerStyle: {
          color: "white",
          fontFamily: "arial",
          fontWeight: "100"
        }
      });
      return;
    } else {
      Toast({
        title: AddressData.message,
        status: "success",
        containerStyle: {
          color: "white",
          fontFamily: "arial",
          fontWeight: "100"
        }
      });

      setStreet(AddressData.street);
      setDistrict(AddressData.district);
      setCity(AddressData.city);
      setState(AddressData.state);
    }
  };

  // useEffect(() => {
  //   setEmail(emailAutoCompleteRef.current.state.value);
  // }, [emailAutoCompleteRef, Email]);
  // * Trigger address search
  useIsomorphicLayoutEffect(() => {
    let Zip = useFilter(Zipcode, { type: "number" });
    if (Zip.length === 8) {
      // * Search Zipcode and Address
      SearchAddress();
    }
  }, [Zipcode]);
  // * Submit form
  const SubmitForm = async (e): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();

    const Inputs = [Name, Birthdate, Celphone, Zipcode, Street, Number, District, City, State];
    switch (true) {
      case /\s/.test(Name) === false:
        Toast({
          title: "Informe seu nome completo",
          status: "error",
          containerStyle: {
            color: "white",
            fontFamily: "arial",
            fontWeight: "100"
          }
        });
        return;

      case Birthdate.length < 10:
        Toast({
          title: "Informe sua data de nascimento",
          status: "error",
          containerStyle: {
            color: "white",
            fontFamily: "arial",
            fontWeight: "100"
          }
        });
        return;

      case useEmpty(Celphone):
        Toast({
          title: "Informe seu telefone",
          status: "error",
          containerStyle: {
            color: "white",
            fontFamily: "arial",
            fontWeight: "100"
          }
        });
        return;

      case useEmpty(Phone):
        Toast({
          title: "Informe seu telefone fixo, caso não possua, coloque o mesmo do celular",
          status: "error",
          containerStyle: {
            color: "white",
            fontFamily: "arial",
            fontWeight: "100"
          }
        });
        return;
      // * Check if passwords are equal
      case Password !== ConfirmPassword:
        Toast({
          title: "Senhas não conferem",
          status: "error",
          containerStyle: {
            color: "white",
            fontFamily: "arial",
            fontWeight: "100"
          }
        });
        return;

      // * Check if Pass is valid
      case Password.length < 6:
        Toast({
          title: "Senha deve ter no mínimo 6 caracteres",
          status: "error",
          containerStyle: {
            color: "white",
            fontFamily: "arial",
            fontWeight: "100"
          }
        });
        return;

      // * Check if Address is informed
      case useEmpty(Street) || useEmpty(District) || useEmpty(City) || useEmpty(State):
        Toast({
          title: "Informe o endereço completo",
          status: "error",
          containerStyle: {
            color: "white",
            fontFamily: "arial",
            fontWeight: "100"
          }
        });
        return;

      // * Check if inputs are valid
      case Inputs.some((input) => useEmpty(input)):
        Toast({
          title: "Preencha todos os campos",
          status: "error",
          containerStyle: {
            color: "white",
            fontFamily: "arial",
            fontWeight: "100"
          }
        });
        return;
      case !useCpf(Document).valid:
        Toast({
          title: "CPF inválido",
          status: "error",
          containerStyle: {
            color: "white",
            fontFamily: "arial",
            fontWeight: "100"
          }
        });
        return;
    }

    const phoneNum = Phone.replace(/[^0-9]/g, "");
    const cellphoneNum = Celphone.replace(/[^0-9]/g, "");

    // * Call API to register user
    setSubmitLoading(true);
    const [RegisterData, RegisterError] = await useRegisterPFClient({
      cpf: Document,
      email: Email,
      name: Name,
      date: Birthdate,
      phone: phoneNum,
      celphone: cellphoneNum,
      password: Password,
      zipcode: Zipcode,
      number: Number,
      street: Street,
      district: District,
      city: City,
      state: State,
      complement: Complement
    });

    // * If Requisition has error
    if (RegisterError) {
      setSubmitLoading(false);
      Toast({
        title: "Erro ao tentar cadastrar usuário, tente novamente.",
        description: RegisterError[1],
        status: "error",
        duration: 5000,
        containerStyle: {
          color: "white",
          fontFamily: "arial",
          fontWeight: "100"
        }
      });
      return;
    } else {
      // * If Requisition has data and ok, then we make the login request
      await signIn({
        UserEmail: Email,
        password: Password
      })
        .then(({ message, status }) => {
          if (status) {
            Toast({
              title: "Usuário cadastrado com sucesso",
              description: checkout
                ? "Aguarde para finalizar a compra"
                : "Aguarde para ser redirecionado",
              status: "success",
              containerStyle: {
                color: "white",
                fontFamily: "arial",
                fontWeight: "100"
              },
              onCloseComplete: () => {
                Router.push(checkout ? "/checkout/entrega" : "/minhaconta");
              }
            });
          } else {
            throw new Error(message);
          }
        })
        .catch(() => {
          Toast({
            title: "Cadastro realizado com sucesso",
            description: "Por gentileza realize o login na tela que se abrirá",
            status: "info",
            containerStyle: {
              color: "white"
            }
          });
        })
        .finally(() => setSubmitLoading(false));
    }
  };

  useEffect(() => {
    // console.log(Birthdate);
  }, [Birthdate]);
  // #VALIDATE SURNAME

  // Main return statement
  return (
    <>
      {/* Main content */}
      <Flex direction="column" as="form" w={["100%", customWidth]} onSubmit={SubmitForm}>
        {/* FORM FIELDS */}
        {/* CPF &~ FORWARD BUTTON */}
        <Flex direction={["column", "row"]}>
          <InputForm
            name="Document"
            value={Document}
            p={3}
            py={2}
            onChange={(e: { target: { value: string } }) => {
              setDocument(e.target.value);
            }}
            placeholder="CPF"
            type="tel"
            id={"RegisterDocumentPF"}
            mask={"999.999.999-99"}
            w={"full"}
            mb="1rem"
          />
        </Flex>
        {/* EMAIL */}
        <Flex direction={"column"} mb={"1rem"} position={"relative"}>
          <VStack>
            <InputComponentEmail email={Email} setEmail={setEmail} />
          </VStack>
          {WideVersion && (
            <Button
              mt={"10px"}
              variant={"primary-button"}
              isLoading={ForwardLoading}
              isDisabled={ForwardDisable}
              onClick={UserExists}
              loadingText="Verificando..."
              _loading={{ color: "white" }}
              _hover={{ opacity: "80%" }}
            >
              <Text textStyle="Bold" color="white">
                {"VERIFICAR"}
              </Text>
            </Button>
          )}
        </Flex>
        {!WideVersion && (
          <Button
            variant={"primary-button"}
            type="button"
            isLoading={ForwardLoading}
            isDisabled={ForwardDisable}
            onClick={UserExists}
            loadingText="Verificando..."
            _loading={{ color: "white" }}
          >
            <Text textStyle="Bold" color="white">
              {"VERIFICAR"}
            </Text>
          </Button>
        )}

        {/* PERSON TITLE */}
        <Box position={"relative"}>
          <Box
            display={HasRegister ? "block" : "none"}
            zIndex={400}
            w={"full"}
            h={"full"}
            bg={"white"}
            opacity={0.6}
            position={"absolute"}
          ></Box>

          <Flex my="1rem">
            <Text
              fontSize="h3"
              color={HasRegister ? "gray.300" : "ll-blue.main"}
              letterSpacing={"-1px"}
            >
              Dados pessoais
            </Text>
          </Flex>

          {/* NAME & BIRTHDATE */}
          <Flex direction={["column", "row"]} mb="1rem" gap="1rem" w={"full"}>
            <InputForm
              border={"1px solid #ECECEC"}
              borderRadius={"50px"}
              name="Name"
              id={"RegisterNamePF"}
              p={3}
              py={2}
              value={Name}
              onChange={(e: { target: { value: string } }) => {
                setName(e.target.value);
              }}
              placeholder="Nome completo"
              type="text"
              width={["full", "70%"]}
              isDisabled={HasRegister}
              fontSize={"16px"}
            />
            <InputForm
              border={"1px solid #ECECEC"}
              borderRadius={"50px"}
              name="Birthdate"
              id={"RegisterBirthPF"}
              color={"#A9A9A9"}
              // borderColor={Birthdate.length > 0 ? "" : "#FF003A"}
              p={3}
              py={2}
              value={Birthdate}
              onChange={(e: { target: { value: string } }) => {
                setBirthdate(e.target.value);
              }}
              placeholder="Nascimento"
              isDisabled={HasRegister}
              type="tel"
              mask={"99/99/9999"}
              width={["full", "30%"]}
              fontSize={"16px"}
            />
          </Flex>

          {/* PHONE & CELPHONE */}
          <Flex mb="1rem" gap="1rem" w={"full"}>
            <InputForm
              border={"1px solid #ECECEC"}
              borderRadius={"50px"}
              name="Celphone"
              id={"RegisterCellPhonePF"}
              p={3}
              py={2}
              mask={
                Celphone && Celphone.split("")[5] === "9" ? `(99) 99999-9999` : `(99) 9999-9999`
              }
              value={Celphone}
              onChange={(e: { target: { value: string } }) => {
                setCelphone(e.target.value);
              }}
              placeholder="Celular"
              type="tel"
              width={"50%"}
              isDisabled={HasRegister}
              fontSize={"16px"}
            />
            <InputForm
              border={"1px solid #ECECEC"}
              borderRadius={"50px"}
              name="Phone"
              mask={Phone && Phone.split("")[5] === "9" ? `(99) 99999-9999` : `(99) 9999-9999`}
              id={"RegisterPhonePF"}
              p={3}
              py={2}
              value={Phone}
              onChange={(e: { target: { value: string } }) => {
                setPhone(e.target.value);
              }}
              placeholder="Telefone"
              type="tel"
              width={"50%"}
              isDisabled={HasRegister}
              fontSize={"16px"}
            />
          </Flex>

          {/* PASSWORD AND CONFIMATION */}
          <Flex direction={["column", "column"]} mb=".7rem" gap="1rem" w={"full"}>
            <Flex
              w={"full"}
              bgColor={"ll-gray.600"}
              border="1px solid"
              borderColor="ll-gray.100"
              borderRadius={"50px"}
            >
              <InputForm
                border={"0px"}
                borderRadius={"50px"}
                name="Password"
                id={"RegisterPasswordPF"}
                value={Password}
                p={3}
                py={2}
                onChange={(e: { target: { value: string } }) => {
                  setPassword(e.target.value);
                }}
                placeholder="Senha"
                type={ShowPassword ? "text" : "password"}
                width={["full", "full"]}
                isDisabled={HasRegister}
              ></InputForm>
              <Img
                cursor={"pointer"}
                mx={["10px", "10px"]}
                w={["25px", "25px"]}
                alt={"Alternative to Image"}
                src={
                  ShowPassword
                    ? "https://cdn.pneufree.com.br/LINGLONG/images/Site/icons/geral/password-hidden.svg"
                    : "https://cdn.pneufree.com.br/LINGLONG/images/Site/icons/geral/password-nohidden.svg"
                }
                onClick={setShow.toggle}
                _focus={{ border: "none" }}
              />
            </Flex>

            <Flex
              w={"full"}
              bgColor={"ll-gray.600"}
              border="1px solid"
              borderColor="ll-gray.100"
              borderRadius={"50px"}
            >
              <InputForm
                border={"0px"}
                borderRadius={"50px"}
                id={"RegisterPasswordPF"}
                name="ConfirmPassword"
                value={ConfirmPassword}
                p={3}
                py={2}
                onChange={(e: { target: { value: string } }) => {
                  setConfirmPassword(e.target.value);
                }}
                placeholder="Confirmar senha"
                type={ShowPassword ? "text" : "password"}
                isDisabled={HasRegister}
                width={["full", "full"]}
              />
              <Img
                cursor={"pointer"}
                mx={["10px", "10px"]}
                w={["25px", "25px"]}
                alt={"Alternative to Image"}
                src={
                  ShowPassword
                    ? "https://cdn.pneufree.com.br/LINGLONG/images/Site/icons/geral/password-hidden.svg"
                    : "https://cdn.pneufree.com.br/LINGLONG/images/Site/icons/geral/password-nohidden.svg"
                }
                onClick={setShow.toggle}
                _focus={{ border: "none" }}
              />
            </Flex>
          </Flex>

          {/* ADDRESS TITLE */}
          <Flex mt="1rem" mb={0}>
            <Text
              fontSize="h3"
              color={HasRegister ? "gray.300" : "ll-blue.main"}
              letterSpacing={"-1px"}
            >
              {"Endereço"}
            </Text>
          </Flex>

          {/* ZIPCODE & DESCRIPTION */}
          <Flex my="1rem" gap="1rem" w={"full"} alignItems={"start"} direction={["row", "column"]}>
            <InputForm
              border={"1px solid #ECECEC"}
              // borderColor={Zipcode.length > 0 ? "" : "#FF003A"}
              borderRadius={"50px"}
              name="Zipcode"
              id={"RegisterZipcodePF"}
              p={3}
              py={2}
              value={Zipcode}
              onChange={(e: { target: { value: string } }) => {
                setZipcode(e.target.value);
              }}
              placeholder="CEP"
              type="tel"
              width={["full", "full"]}
              mask={"99.999-999"}
              isDisabled={HasRegister}
            />
            <Text
              color="#58585A"
              fontSize={["text3", "11px"]}
              ml={"15px"}
              mt={WideVersion && "-10px"}
            >
              {"Insira seu CEP para preencher o endereço"}
            </Text>
          </Flex>

          {/* STREET, NUMBER & COPLEMENT */}
          <Flex direction={["column", "row"]} w={"full"} mb="1rem" gap="1rem">
            <InputForm
              border={"1px solid #ECECEC"}
              // borderColor={Street.length > 0 ? "" : "#FF003A"}
              borderRadius={"50px"}
              p={3}
              py={2}
              name="Street"
              id={"RegisterStreetPF"}
              value={Street}
              onChange={(e: { target: { value: string } }) => {
                setStreet(e.target.value);
              }}
              placeholder="Rua"
              type="text"
              minW={"14.85rem"}
              width={["full", "100%"]}
            />
          </Flex>

          {/* DISTRICT, CITY, STATE */}
          <Flex direction={["column", "row"]} mb="1rem" gap="1rem">
            <InputForm
              border={"1px solid #ECECEC"}
              borderRadius={"50px"}
              // borderColor={Number.length > 0 ? "" : "#FF003A"}
              p={3}
              py={2}
              name="Number"
              id={"RegisterNumberPF"}
              value={Number}
              onChange={(e: { target: { value: string } }) => {
                setNumber(e.target.value);
              }}
              placeholder="Número"
              type="tel"
              width={["full", "30%"]}
              isDisabled={HasRegister && Disable}
            />
            <InputForm
              border={"1px solid #ECECEC"}
              borderRadius={"50px"}
              // borderColor={"#FF003A"}
              p={3}
              py={2}
              name="Complement"
              id={"RegisterCompPF"}
              value={Complement}
              onChange={(e: { target: { value: string } }) => {
                setComplement(e.target.value);
              }}
              placeholder="Complemento"
              type="text"
              width={["full", "70%"]}
              isDisabled={HasRegister && Disable}
            />
          </Flex>
          <Text color="#58585A" fontSize={["text3", "11px"]} ml={"15px"} mt={"-10px"} mb={"10px"}>
            Caso não tenha um número, defina como "S/N"
          </Text>
          <Flex direction={["column", "column"]} mb="1rem" gap="1rem">
            <InputForm
              border={"1px solid #ECECEC"}
              borderRadius={"50px"}
              // borderColor={District.length > 0 ? "" : "#FF003A"}
              name="District"
              minW={"14.85rem"}
              id={"RegisterDistrictPF"}
              p={3}
              py={2}
              value={District}
              onChange={(e: { target: { value: string } }) => {
                setDistrict(e.target.value);
              }}
              placeholder="Bairro"
              type="text"
              width={["full", "full"]}
            />
            <InputForm
              border={"1px solid #ECECEC"}
              borderRadius={"50px"}
              // borderColor={City.length > 0 ? "" : "#FF003A"}
              name="City"
              id={"RegisterCityPF"}
              p={3}
              py={2}
              value={City}
              onChange={(e: { target: { value: string } }) => {
                setCity(e.target.value);
              }}
              placeholder="Cidade"
              type="text"
              width={["full", "full"]}
              isDisabled={true}
            />
            <Flex gap="1rem">
              <InputForm
                border={"1px solid #ECECEC"}
                borderRadius={"50px"}
                // borderColor={State.length > 0 ? "" : "#FF003A"}
                id={"RegisterStatePF"}
                p={3}
                py={2}
                name="State"
                value={State}
                onChange={(e: { target: { value: string } }) => {
                  setState(e.target.value);
                }}
                placeholder="Estado"
                type="text"
                width={["full", "100%"]}
                isDisabled={true}
              />
            </Flex>
          </Flex>
          {/* BUTTONS */}
          <Button
            variant={"primary-button"}
            w={"full"}
            h={"40px"}
            mb="2rem"
            type="button"
            isLoading={SubmitLoading}
            isDisabled={SubmitDisable}
            onClick={SubmitForm}
            _hover={{ opacity: "80%" }}
          >
            <Text fontStyle="Bold" color="white">
              {"PROSSEGUIR"}
            </Text>
          </Button>
        </Box>
      </Flex>
      {/* Modal to user that has register */}
      <ModalLogin
        close={onClose}
        open={isOpen}
        doc={Document}
        isPF={true}
        email={Email}
        existDoc={ExistDoc}
        existEmail={ExistEmail}
        Msg={ExistMsg}
      />
    </>
  );
}
